import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchCertificatesAsync,
  setFilters,
  clearFilters,
  setPage,
  clearError,
  downloadCertificateAsync
} from '../store/slices/certificatesSlice';
import LoadingSpinner from './LoadingSpinner';
import './Students.css'; // Reusing similar styles
import './Certificates.css'; // Certificate-specific styles

const Certificates: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { certificates, loading, error, filters, pagination } = useAppSelector((state) => state.certificates);

  const [searchInput, setSearchInput] = useState(filters?.search || '');
  const [selectedServiceType, setSelectedServiceType] = useState(filters?.serviceType || '');
  const [dateFrom, setDateFrom] = useState(filters?.dateFrom || '');
  const [dateTo, setDateTo] = useState(filters?.dateTo || '');

  const serviceTypes = [
    { value: '', label: 'All Service Types' },
    { value: 'Индивидуальное обучение', label: 'Индивидуальное обучение' },
    { value: 'Групповые занятия', label: 'Групповые занятия' }
  ];

  // Load certificates when component mounts or filters change
  const loadCertificates = useCallback(() => {
    if (!pagination) return;
    
    dispatch(fetchCertificatesAsync({
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      search: filters?.search || '',
      serviceType: filters?.serviceType || '',
      dateFrom: filters?.dateFrom || '',
      dateTo: filters?.dateTo || ''
    }));
  }, [
    dispatch, 
    pagination?.currentPage, 
    pagination?.itemsPerPage,
    filters?.search,
    filters?.serviceType,
    filters?.dateFrom,
    filters?.dateTo
  ]);

  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  const handleSearch = () => {
    dispatch(setFilters({
      search: searchInput,
      serviceType: selectedServiceType,
      dateFrom,
      dateTo
    }));
    dispatch(setPage(1)); // Reset to first page when searching
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSelectedServiceType('');
    setDateFrom('');
    setDateTo('');
    dispatch(clearFilters());
    dispatch(setPage(1));
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleDownload = async (certificateId: number) => {
    try {
      await dispatch(downloadCertificateAsync(certificateId)).unwrap();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const formatAmount = (amount: string) => {
    return `${amount} BYN`;
  };



  if (loading && certificates.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Certificates</h1>
          <p>Manage certificates and generate new ones</p>
        </div>
        <button
          onClick={() => navigate('/certificates/create')}
          className="btn-primary"
        >
          Create Certificate
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="filter-bar">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by student name..."
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />

        <select
          value={selectedServiceType}
          onChange={(e) => setSelectedServiceType(e.target.value)}
          className="form-select"
        >
          {serviceTypes.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>

        <label htmlFor="date-from" className="filter-label">From</label>
        <input
          id="date-from"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="form-input"
        />

        <label htmlFor="date-to" className="filter-label">To</label>
        <input
          id="date-to"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="form-input"
        />

        <button onClick={handleSearch} className="btn-primary">
          Search
        </button>
        <button onClick={handleClearFilters} className="btn-secondary">
          Clear
        </button>
      </div>

      {/* Certificates Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Cert #</th>
              <th>Student</th>
              <th>Service Type</th>
              <th>Lessons</th>
              <th>Amount</th>
              <th>Payment Date</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(!certificates || certificates.length === 0) && !loading ? (
              <tr>
                <td colSpan={8} className="no-data">
                  No certificates found
                </td>
              </tr>
            ) : (
              certificates?.map((certificate) => (
                <tr key={certificate.id}>
                  <td className="font-mono">{certificate.certNumber}</td>
                  <td>
                    {certificate.transaction.fullName}
                  </td>
                  <td>
                    {certificate.serviceType}
                  </td>
                  <td className="text-center">{certificate.lessonsCount}</td>
                  <td className="text-right font-mono">
                    {formatAmount(certificate.transaction.amount)}
                  </td>
                  <td>{formatDate(certificate.transaction.paymentDate)}</td>
                  <td>{formatDate(certificate.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleDownload(certificate.id)}
                        className="btn-action btn-download"
                        title="Download PDF"
                      >
                        📥
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {loading && certificates.length > 0 && (
          <div className="loading-overlay">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage <= 1}
            className="pagination-btn"
          >
            Previous
          </button>

          <div className="pagination-info">
            Page {pagination.currentPage} of {pagination.totalPages}
            ({pagination.totalItems} total)
          </div>

          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage >= pagination.totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

    </div>
  );
};

export default Certificates; 